import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Paper,
  Typography,
  Box,
  Stack,
  IconButton,
  Tooltip,
  Switch,
} from '@mui/material';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CopyrightIcon from '@mui/icons-material/Copyright';
import CorporateFareOutlinedIcon from '@mui/icons-material/CorporateFareOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { Client } from '../../../data/models/client.model';
import { useClientVertical } from '../../../hooks/display/useClientVertical';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as parentOrganizationSlice from '../../../data/parent-organizations.slice';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom';
import ParentOrganizationDialog from './ParentOrganizationDialog';
import {
  ContactPageOutlined,
  EventOutlined,
  WcOutlined,
} from '@mui/icons-material';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'ClientProfile';

const classes = {
  container: `${PREFIX}-container`,
  details: `${PREFIX}-details`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  row: `${PREFIX}-row`,
  label: `${PREFIX}-label`,
  value: `${PREFIX}-value`,
  benefits: `${PREFIX}-benefits`,
  icon: `${PREFIX}-icon`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flex: 1,
    minWidth: 300,
  },

  [`& .${classes.details}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(3, 4),
  },

  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },

  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontSize: 21,
    fontWeight: 700,
  },

  [`& .${classes.row}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(4),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  [`& .${classes.label}`]: {
    fontSize: 14,
    fontWeight: '400',
    paddingBottom: 0,
  },

  [`& .${classes.value}`]: {
    fontSize: 14,
    fontWeight: 600,
    paddingBottom: 0,
    color: theme.palette.lightActionDisabled90p.main,
    fontFamily: 'Source Sans Pro',
    fontStyle: 'normal',
    lineHeight: '157%',
    letterSpacing: '0.1px',
  },

  [`& .${classes.benefits}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.icon}`]: {
    color: theme.palette.lightActionDisabled60p.main,
  },
}));

interface ClientProfileProps {
  client?: Client;
  edit: (client: Client) => void;
  activate: (client: Client) => void;
  deactivate: (client: Client) => void;
  readonly: boolean;
}

export default function ClientProfile({
  client,
  edit,
  activate,
  deactivate,
  readonly,
}: ClientProfileProps) {
  const t = useCommonTranslation();

  const isSuperReaderUser = useIsSuperReaderUser();

  const [parentOrganizationDialogOpen, setParentOrganizationDialogOpen] =
    React.useState(false);

  const dispatch = useAppDispatch();
  const organization = useAppSelector(
    parentOrganizationSlice.selectCurrentOrganization,
  );

  React.useEffect(() => {
    if (client?.parentOrganizationBC) {
      dispatch(
        parentOrganizationSlice.fetchOrganizationAsync(
          client.parentOrganizationBC,
        ),
      );
    }
  }, [client?.parentOrganizationBC, dispatch]);

  const formatDate = (date?: string) => {
    if (date) {
      return DateTime.fromISO(date).toLocaleString(DateTime.DATE_SHORT);
    }
    return '';
  };

  const handleActiveChange = (evt: any, client: Client) => {
    if (evt.target.checked) {
      activate(client);
    } else {
      deactivate(client);
    }
  };

  return (
    <StyledBox className={classes.container}>
      <Paper elevation={2} className={classes.details}>
        <Box className={classes.header}>
          <Typography className={classes.title}>
            {t('client.profile')}
          </Typography>
          {!readonly && client && !isSuperReaderUser && (
            <Box>
              <Tooltip title={t('client.editProfile')}>
                <span>
                  <IconButton
                    data-testid="delete-address"
                    onClick={(evt) => edit(client!)}
                    color="primary"
                    size="large"
                    disabled={!client || client.state === 'active'}>
                    <EditOutlinedIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  client.state === 'active'
                    ? t('client.active')
                    : t('client.inactive')
                }>
                <Switch
                  checked={client.state === 'active'}
                  onChange={(evt) => handleActiveChange(evt, client)}
                />
              </Tooltip>
            </Box>
          )}
        </Box>
        <Box className={classes.row}>
          <Stack direction="row" alignItems="center" gap={1}>
            <BadgeOutlinedIcon className={classes.icon} />
            <Typography className={classes.label}>
              {t('client.clientBC')}
            </Typography>
          </Stack>
          <Typography className={classes.value}>{client?.clientBC}</Typography>
        </Box>
        <Box className={classes.row}>
          <Stack direction="row" alignItems="center" gap={1}>
            <CopyrightIcon className={classes.icon} />
            <Typography className={classes.label}>
              {t('client.clientLegalName')}
            </Typography>
          </Stack>
          <Typography className={classes.value}>{client?.legalName}</Typography>
        </Box>
        <Box className={classes.row}>
          <Stack direction="row" alignItems="center" gap={1}>
            <WcOutlined className={classes.icon} />
            <Typography className={classes.label}>
              {t('client.abbreviation')}
            </Typography>
          </Stack>
          <Typography className={classes.value}>
            {client?.clientAbbreviation}
          </Typography>
        </Box>
        <Box className={classes.row}>
          <Stack direction="row" alignItems="center" gap={1}>
            <EventOutlined className={classes.icon} />
            <Typography className={classes.label}>
              {t('client.effectiveStartDate')}
            </Typography>
          </Stack>
          <Typography className={classes.value}>
            {formatDate(client?.effectiveStartDate)}
          </Typography>
        </Box>
        <Box className={classes.row}>
          <Stack direction="row" alignItems="center" gap={1}>
            <EventOutlined className={classes.icon} />
            <Typography className={classes.label}>
              {t('client.effectiveEndDate')}
            </Typography>
          </Stack>
          <Typography className={classes.value}>
            {formatDate(client?.effectiveEndDate)}
          </Typography>
        </Box>
        <Box className={classes.row}>
          <Stack direction="row" alignItems="center" gap={1}>
            <CorporateFareOutlinedIcon className={classes.icon} />
            <Typography className={classes.label}>
              {t('client.vertical')}
            </Typography>
          </Stack>
          <Typography className={classes.value}>
            {useClientVertical(client?.clientVerticalBC)}
          </Typography>
        </Box>
        <Box className={classes.row}>
          <Stack direction="row" alignItems="center" gap={1}>
            <CorporateFareOutlinedIcon className={classes.icon} />
            <Typography className={classes.label}>
              {t('parentOrganizations.parentOrganization')}
            </Typography>
          </Stack>
          {client?.parentOrganizationBC && (
            <Box>
              <Typography
                className={classes.value}
                to={`/parent-organizations/${encodeURIComponent(client.parentOrganizationBC)}`}
                component={Link}
                sx={(theme) => ({
                  textDecoration: 'none',
                  color: theme.palette.primary.main,
                })}>
                {organization?.parentOrganizationName}
              </Typography>
              {!isSuperReaderUser && (
                <Tooltip title={t('client.editParentOrganization')}>
                  <span>
                    <IconButton
                      data-testid="edit-parent-org"
                      onClick={(_evt) => setParentOrganizationDialogOpen(true)}
                      color="primary"
                      size="small"
                      disabled={!client}>
                      <EditOutlinedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
        <Box className={classes.row}>
          <Stack direction="row" alignItems="center" gap={1}>
            <ContactPageOutlined className={classes.icon} />
            <Typography className={classes.label}>
              {t('client.isInternal')}
            </Typography>
          </Stack>
          <Typography className={classes.value}>
            {client?.isInternal ? t('common.yes') : t('common.no')}
          </Typography>
        </Box>
      </Paper>
      {/* <PassengerBenefits servicePlans={servicePlans} /> */}
      <ParentOrganizationDialog
        open={parentOrganizationDialogOpen}
        client={client}
        close={() => setParentOrganizationDialogOpen(false)}
      />
    </StyledBox>
  );
}
